import React from 'react';
import './index.css';
import { DeliverySession, GroupOrder } from '../../../../../../hooks/useDeliverySessionData';
import RouteMap from '../RouteMap';
import CalculatedRouteMap from '../CalculatedRouteMap';
import { Spinner } from 'react-bootstrap';

interface DeliverySessionDetailProps {
  session: DeliverySession;
  groupOrder?: GroupOrder | null;
  onBack: () => void;
  loading?: boolean;
  filterDate?: string;
}

const DeliverySessionDetail: React.FC<DeliverySessionDetailProps> = ({
  session,
  groupOrder,
  onBack,
  loading = false,
  filterDate = '',
}) => {

  // Format the date for display (DD/MM/YYYY)
  const formatDisplayDate = (dateString: string): string => {
    if (!dateString) return 'No date selected';
    try {
      const [year, month, day] = dateString.split('-');
      if (!year || !month || !day) return dateString;
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Actual route: sessionStartPoint + locationPoints + sessionEndPoint
  const actualPoints = [
    session.sessionStartPoint,
    ...session.locationPoints,
    session.sessionEndPoint,
  ];

  // Calculated route (hub-based):
  const hubLocation = groupOrder?.deliveryRoute?.hubLocation ?? null;
  const orderLocations = groupOrder
    ? groupOrder.deliveryRoute.route.map(r => r.location)
    : [];

  if (loading) {
    return (
      <div className="delivery-session-detail text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading session details...</p>
      </div>
    );
  }

  return (
    <div className="delivery-session-detail">
      {/* Top bar */}
      <div className="delivery-session-detail__top-bar">
        <button onClick={onBack} className="delivery-session-detail__back-button">
          ← Back to Sessions
        </button>
        
        <div 
          className="delivery-session-detail__filter-date"
          style={{
            marginLeft: 'auto',
            marginRight: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: '#e9ecef',
            padding: '0.25rem 0.75rem',
            borderRadius: '20px',
            fontSize: '0.9rem'
          }}
        >
          <span style={{ color: '#6c757d', fontWeight: 500 }}>
            Selected Date:
          </span>
          <span style={{ color: '#495057', fontWeight: 600 }}>
            {formatDisplayDate(filterDate)}
          </span>
        </div>
        
        <div className="delivery-session-detail__divider" />
      </div>

      <div className="delivery-session-detail__content">
        {/* Distance cards */}
        <div className="delivery-session-detail__distance-grid">
          <div className="delivery-session-detail__distance-card delivery-session-detail__distance-card--delivery">
            <div className="delivery-session-detail__distance-label">Delivery Distance</div>
            <div className="delivery-session-detail__distance-number">
              {session.deliveryDistance}
              <span className="delivery-session-detail__distance-unit">km</span>
            </div>
            <div className="delivery-session-detail__distance-note">Actual GPS-tracked distance</div>
          </div>
          <div className="delivery-session-detail__distance-card delivery-session-detail__distance-card--total">
            <div className="delivery-session-detail__distance-label">Total Distance</div>
            <div className="delivery-session-detail__distance-number">
              {session.totalDistance}
              <span className="delivery-session-detail__distance-unit">km</span>
            </div>
            <div className="delivery-session-detail__distance-note">Calculated expected distance</div>
          </div>
        </div>

        {/* Dual maps */}
        <div className="delivery-session-detail__maps-container">

          {/* Actual Route Map */}
          <div className="delivery-session-detail__map-wrapper">
            <div className="delivery-session-detail__map">
              <RouteMap
                points={actualPoints}
                accentColor="#3b82f6"
                label="ACTUAL ROUTE"
                subtitle={`deliveryDistance · ${session.deliveryDistance} km`}
                distance={session.deliveryDistance}
                sessionEndPoint={session.sessionEndPoint}
              />
            </div>
          </div>

          {/* Calculated Route Map (hub-based) */}
          <div className="delivery-session-detail__map-wrapper">
            <div className="delivery-session-detail__map">
              {groupOrder && hubLocation ? (
                <CalculatedRouteMap
                  hubLocation={hubLocation}
                  orderLocations={orderLocations}
                  accentColor="#8b5cf6"
                  label="CALCULATED ROUTE"
                  subtitle={`distanceInKm: ${groupOrder.deliveryRoute.distanceInKm} km · ${groupOrder.deliveryRoute.totalDuration}`}
                  distance={groupOrder.deliveryRoute.distanceInKm}
                />
              ) : groupOrder && !hubLocation ? (
                // fallback: group order exists but no hubLocation (old data)
                <CalculatedRouteMap
                  hubLocation={orderLocations[0] ?? { lat: 0, lng: 0 }}
                  orderLocations={orderLocations.slice(1)}
                  accentColor="#8b5cf6"
                  label="CALCULATED ROUTE"
                  subtitle={`distanceInKm: ${groupOrder.deliveryRoute.distanceInKm} km · ${groupOrder.deliveryRoute.totalDuration}`}
                  distance={groupOrder.deliveryRoute.distanceInKm}
                />
              ) : (
                <div className="delivery-route-map delivery-route-map--empty">
                  <span className="delivery-route-map__empty-text">No group order data available</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliverySessionDetail;